import { SelectIdentity } from '@startuptools/business-ui/identity/select/SelectIdentity';
import { Trans, useLingui } from '@lingui/react';
import { Controller, useForm } from 'react-hook-form';
import { Stack } from '@startuptools/ui/Stack';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Button } from '@startuptools/ui/Button';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { Box } from '@startuptools/ui/Box';
import { Input } from '@startuptools/ui/Input';
import { IconButton } from '@startuptools/ui/IconButton';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  CreateTransactionDocument,
  useCreateTransactionSuspenseQuery,
  useStCreateTransferMutation,
  useStCreateIssueMutation,
  useStCreateUnissueMutation,
} from './create-transaction.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import {
  GqlCompany,
  GqlCurrency,
  type GqlShareClass,
  type GqlShareTransactionGroup,
  type GqlSnrShareCapitalChange,
  GqlTransactionKind,
} from '../../../../../graphql/base-types.graphql';
import { ShareTransactionGroupViewDocument } from '../transaction-group.graphql';
import React from 'react';
import { RichTextInput } from '@startuptools/ui/RichTextInput';
import { useCreateShareholderMutation } from '../../../../../graphql/react-operations';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { DateTime } from 'luxon';
import { isNil } from 'lodash-es';
import { stripeProducts } from '../../../../../../environments/environment';
import {
  FREE_SHAREHOLDERS_LIMIT,
  STARTER_SHAREHOLDERS_LIMIT,
  UpgradeBanner,
} from '@startuptools/business-ui/UpgradeBanner';
import { shareClassToInput } from '../../../../../helpers/share-class-to-input';
import { currenciesShort } from '../../../../../components/currencies';
import { useNavigate } from '../../../../../components/Navigate';

type FormData = {
  sellerId: string;
  buyerId: string;
  numberOfShares: string;
  shareClassFrom: string;
  shareClassTo: string;
  shareClassName: string | undefined;
  shareClassVoteWeight: string | undefined;
  currency: GqlCurrency;
  pricePerShare: string;
  comment: string;
};

const generateBlockPartionComment = (date: string) => {
  return `Den ${DateTime.fromISO(date).toISODate()} meddelade aktieägaren [NAMN PÅ SÄLJAREN] planerad försäljning av dessa aktier till [nuvarande/nya] aktieägare [NAMN PÅ KÖPAREN]. Föreslagen köpeskilling är [PRIS] SEK/aktie. Den [DATUM] skickade styrelsen meddelande om ovan till samtliga aktieägare samt informerade att aktieägare kan anmäla förköp senast [DATUM]. Den [DATUM] anmälde [NAMN PÅ AKTIEÄGARE SOM ÖNSKAR UTNYTTJA FÖRKÖP] att förköpsrätt nyttjas.`;
};

export const CreateTransaction = ({
  company,
  stg,
  onAllSharesAssigned,
}: {
  company: Pick<GqlCompany, 'id' | 'currency' | 'shareRegisterLimit'>;
  stg: Pick<GqlShareTransactionGroup, 'id' | 'transactionDate' | 'sortOrder' | 'kind'> & {
    snrShareCapitalChange?:
      | Pick<
          GqlSnrShareCapitalChange,
          'shareClasses' | 'sharesLeftToAllocate' | 'shareValue' | 'sharesLeftToAllocateByShareClass' | 'currency'
        >
      | null
      | undefined;
  };
  onAllSharesAssigned: () => void;
}) => {
  const { navigate } = useNavigate();
  const { control, watch, handleSubmit, setValue, reset, resetField, clearErrors } = useForm<FormData>({
    defaultValues: {
      numberOfShares: '',
      shareClassFrom: '',
      shareClassTo: '',
      pricePerShare: stg.snrShareCapitalChange?.shareValue ?? '',
      currency: stg.snrShareCapitalChange?.currency ?? company.currency,
      comment: stg.kind === GqlTransactionKind.BlockPartition ? generateBlockPartionComment(stg.transactionDate) : '',
    },
    mode: 'onSubmit',
  });
  const { i18n } = useLingui();
  const companyId = useCompanyId();
  const { sellerId, shareClassFrom, shareClassTo, buyerId } = watch();
  const [createTransfer, { loading: createTransferLoading }] = useStCreateTransferMutation({
    refetchQueries: [
      { query: ShareTransactionGroupViewDocument, variables: { companyId, id: stg.id } },
      CreateTransactionDocument,
    ],
  });
  const [createIssue, { loading: createIssueLoading }] = useStCreateIssueMutation({
    refetchQueries: [
      { query: ShareTransactionGroupViewDocument, variables: { companyId, id: stg.id } },
      CreateTransactionDocument,
    ],
  });

  const [createUnIssue, { loading: createUnIssueLoading }] = useStCreateUnissueMutation({
    refetchQueries: [
      { query: ShareTransactionGroupViewDocument, variables: { companyId, id: stg.id } },
      CreateTransactionDocument,
    ],
  });

  // eslint-disable-next-line sut/loading-mutation
  const [createSh] = useCreateShareholderMutation({
    refetchQueries: [CreateTransactionDocument],
    awaitRefetchQueries: true,
  });
  const [success, setSuccess] = React.useState(false);
  const [_, startTransition] = React.useTransition();
  const [localSellerId, setLocalSellerId] = React.useState<string | undefined>();

  const {
    data: { companySubscription, shCount, ...data },
  } = useCreateTransactionSuspenseQuery({
    variables: {
      companyId,
      input: {
        date: stg.transactionDate,
        sortOrder: stg.sortOrder,
      },
      fetchShState: Boolean(localSellerId),
      shInput: {
        id: localSellerId ?? 'undefined',
        date: stg.transactionDate,
        sortOrder: stg.sortOrder,
      },
    },
    fetchPolicy: 'cache-and-network',
  });

  const shareClassesFrom: GqlShareClass[] | undefined = React.useMemo(() => {
    switch (stg.kind) {
      case GqlTransactionKind.Transfer:
      case GqlTransactionKind.ShareClassConvert:
      case GqlTransactionKind.BlockPartition:
      case GqlTransactionKind.Unissue: {
        return data.shSellableShares?.sellableShares.shareClasses;
      }
      case GqlTransactionKind.Issue: {
        return stg.snrShareCapitalChange?.shareClasses.length
          ? stg.snrShareCapitalChange?.shareClasses.filter(
              sc => sc.quantity && sc.quantity.length && parseInt(sc.quantity) > 0,
            )
          : data.shareClasses.length
            ? data.shareClasses.map(s => ({ name: s.name, voteWeight: s.voteWeight }))
            : [{ name: 'STAM', voteWeight: '1', quantity: '' }];
      }
      default: {
        throw new Error(`transaction class is not supported ${stg.kind}`);
      }
    }
  }, [
    data.shSellableShares?.sellableShares.shareClasses,
    data.shareClasses,
    stg.kind,
    stg.snrShareCapitalChange?.shareClasses,
  ]);

  const onSave = async ({
    buyerId,
    sellerId,
    shareClassFrom: shareClassFromName,
    shareClassTo: shareClassToName,
    shareClassName,
    shareClassVoteWeight,
    numberOfShares,
    currency,
    pricePerShare,
    comment,
  }: FormData) => {
    const shareClassFrom =
      shareClassFromName === 'create-new'
        ? { name: shareClassName!, voteWeight: shareClassVoteWeight! }
        : (shareClassesFrom?.find(sc => sc.name === shareClassFromName) ??
          data.shareClasses
            .map(s => ({ name: s.name, voteWeight: s.voteWeight }))
            .find(sc => sc.name === shareClassFromName));
    const shareClassTo =
      shareClassToName === 'create-new'
        ? { name: shareClassName!, voteWeight: shareClassVoteWeight! }
        : (shareClassesFrom?.find(sc => sc.name === shareClassToName) ??
          data.shareClasses
            .map(s => ({ name: s.name, voteWeight: s.voteWeight }))
            .find(sc => sc.name === shareClassToName));
    const kind = stg.kind;
    switch (kind) {
      case GqlTransactionKind.Transfer:
      case GqlTransactionKind.ShareClassConvert:
      case GqlTransactionKind.BlockPartition: {
        const realShareClassTo =
          stg.kind === GqlTransactionKind.Transfer || stg.kind === GqlTransactionKind.BlockPartition
            ? shareClassFrom
            : shareClassTo;
        await createTransfer({
          variables: {
            companyId,
            input: {
              buyerId:
                stg.kind === GqlTransactionKind.ShareClassConvert || stg.kind === GqlTransactionKind.BlockPartition
                  ? sellerId
                  : buyerId,
              sellerId,
              numberOfShares: parseInt(numberOfShares),
              shareClassFrom: shareClassToInput(shareClassFrom!),
              shareClassTo: shareClassToInput(realShareClassTo!),
              currency,
              pricePerShare: pricePerShare.length ? pricePerShare : null,
              comment,
              shareTransactionGroupId: stg.id,
            },
          },
        });
        break;
      }
      case GqlTransactionKind.Issue:
        if (!shareClassFrom) throw new Error('No shareclass found');
        await createIssue({
          variables: {
            companyId,
            input: {
              buyerId,
              numberOfShares: parseInt(numberOfShares),
              pricePerShare: pricePerShare.length ? pricePerShare : null,
              shareClassTo: shareClassToInput(shareClassFrom),
              shareTransactionGroupId: stg.id,
              currency,
              comment,
            },
          },
        });

        break;
      case GqlTransactionKind.Unissue:
        await createUnIssue({
          variables: {
            companyId,
            input: {
              sellerId,
              numberOfShares: parseInt(numberOfShares),
              pricePerShare: pricePerShare.length ? pricePerShare : null,
              shareClassFrom: shareClassToInput(shareClassFrom!),
              comment,
              shareTransactionGroupId: stg.id,
              currency,
            },
          },
        });
        break;
      case GqlTransactionKind.CurrencyChange:
      case GqlTransactionKind.Merge:
      case GqlTransactionKind.Split:
        throw new Error('Cant manually create transaction for this kind');
    }
    if (
      [GqlTransactionKind.Issue, GqlTransactionKind.Unissue].includes(stg.kind) &&
      data.nextShareCapitalChanges?.afterNext &&
      stg.snrShareCapitalChange?.sharesLeftToAllocate &&
      stg.snrShareCapitalChange?.sharesLeftToAllocate - parseInt(numberOfShares) === 0
    ) {
      onAllSharesAssigned();
    }
    setSuccess(true);

    reset({
      pricePerShare: watch('pricePerShare'),
      shareClassFrom: watch('shareClassFrom'),
      sellerId: '',
      buyerId: '',
      numberOfShares: '',
      shareClassTo: '',
      currency: stg.snrShareCapitalChange?.currency ?? company.currency,
      comment: stg.kind === GqlTransactionKind.BlockPartition ? generateBlockPartionComment(stg.transactionDate) : '',
    });
    clearErrors();
  };
  React.useEffect(() => {
    if (success) {
      const id = setTimeout(() => setSuccess(false), 1000);
      return () => clearTimeout(id);
    }
  }, [reset, success, watch]);

  const availableToAllocate = React.useMemo<number | undefined>(() => {
    switch (stg.kind) {
      case GqlTransactionKind.Transfer:
      case GqlTransactionKind.ShareClassConvert:
      case GqlTransactionKind.BlockPartition: {
        return data.shSellableShares?.sellableShares.shares[shareClassFrom];
      }
      case GqlTransactionKind.Issue:
      case GqlTransactionKind.Unissue: {
        if (!stg.snrShareCapitalChange?.sharesLeftToAllocateByShareClass.length)
          return stg.snrShareCapitalChange?.sharesLeftToAllocate;
        return stg.snrShareCapitalChange?.sharesLeftToAllocateByShareClass.find(sc => sc.name === shareClassFrom)?.left;
      }
      default: {
        throw new Error(`transaction kindnot supported ${stg.kind}`);
      }
    }
  }, [
    data.shSellableShares?.sellableShares.shares,
    shareClassFrom,
    stg.kind,
    stg.snrShareCapitalChange?.sharesLeftToAllocate,
    stg.snrShareCapitalChange?.sharesLeftToAllocateByShareClass,
  ]);

  const loading = createTransferLoading || createIssueLoading || createUnIssueLoading;

  const createShareholder =
    (field: 'sellerId' | 'buyerId'): React.ComponentProps<typeof SelectIdentity>['handleCreate'] =>
    async identity => {
      const { data } = await createSh({
        variables: {
          companyId,
          input: {
            companyId,
            identityInput: identityToInput(identity),
          },
        },
      });
      if (data?.createShareholder.id) {
        setValue(field, data?.createShareholder.id, { shouldValidate: false });
      }
    };

  let numberOfSharesDisabled = false;
  if (stg.kind === GqlTransactionKind.Transfer) {
    numberOfSharesDisabled = !sellerId || !shareClassFrom;
  } else if (stg.kind === GqlTransactionKind.Issue) {
    numberOfSharesDisabled = !buyerId;
  } else if (stg.kind === GqlTransactionKind.Unissue) {
    numberOfSharesDisabled = !sellerId || !shareClassFrom;
  }

  React.useEffect(() => {
    if (shareClassesFrom?.[0]?.name) setValue('shareClassFrom', shareClassesFrom[0].name, { shouldValidate: false });
  }, [setValue, shareClassesFrom, shareClassesFrom?.length]);

  const freeShareholdersLimit =
    stg.kind === GqlTransactionKind.Transfer
      ? (company.shareRegisterLimit ?? FREE_SHAREHOLDERS_LIMIT) + 1
      : (company.shareRegisterLimit ?? FREE_SHAREHOLDERS_LIMIT);

  const showFreeUpgradeBanner = isNil(companySubscription) && shCount.count >= freeShareholdersLimit;

  const showStarterUpgradeBanner =
    !isNil(companySubscription) &&
    companySubscription.mainProduct === stripeProducts.startup &&
    shCount.count >= (company.shareRegisterLimit ?? STARTER_SHAREHOLDERS_LIMIT);

  // We do not yet have the means to differentiate between old and new ones, so let's not enforce it until we can do that.
  // const showGrowUpgradeBanner =
  //   !isNil(companySubscription) &&
  //   companySubscription.mainProduct === stripeProducts.startup &&
  //   shCount.count >= GROW_SHAREHOLDERS_LIMIT;

  const showUpgradeBanner = showFreeUpgradeBanner || showStarterUpgradeBanner; // || showGrowUpgradeBanner;

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <Stack gap={1}>
        {stg.kind !== GqlTransactionKind.Issue && (
          <Controller
            control={control}
            name="sellerId"
            rules={{ required: true }}
            render={({ field: { value, onChange, ...field }, formState }) => {
              return (
                <FormControl error={Boolean(formState.errors?.sellerId)}>
                  <FormLabel>
                    {stg.kind === GqlTransactionKind.ShareClassConvert ? (
                      <Trans id="Shareholder" />
                    ) : (
                      <Trans id="Seller" />
                    )}
                  </FormLabel>
                  <SelectIdentity
                    includeFictitious
                    sx={{ width: '100%' }}
                    allowCreation={false}
                    paywall={
                      showUpgradeBanner ? (
                        <UpgradeBanner
                          feature="share_register"
                          navigateToBilling={() => navigate(`/settings/billing`)}
                          customShareRegisterLimit={company.shareRegisterLimit}
                        />
                      ) : undefined
                    }
                    placeholder={<Trans id="Choose shareholder" />}
                    emptyState={<Trans id="No shareholders found" />}
                    handleCreate={() => {
                      throw new Error('Not allowed');
                    }}
                    identities={data.sellableShareState.sellableShares
                      .filter(s => Boolean(s.shareholder.identity))
                      .map(s => ({
                        ...s.shareholder.identity!,
                        value: s.shareholder.id,
                      }))}
                    {...field}
                    value={value || null}
                    onChange={(_e, value) => {
                      if (value) {
                        startTransition(() => {
                          setLocalSellerId(value as string);
                          onChange(value);
                        });
                      }
                    }}
                  />
                </FormControl>
              );
            }}
          />
        )}
        {![
          GqlTransactionKind.ShareClassConvert,
          GqlTransactionKind.BlockPartition,
          GqlTransactionKind.Unissue,
        ].includes(stg.kind) && (
          <Controller
            control={control}
            name="buyerId"
            rules={{ required: true }}
            render={({ field: { value, ...field }, formState }) => (
              <FormControl error={Boolean(formState.errors?.buyerId)}>
                <FormLabel>
                  <Trans id="Buyer" />
                </FormLabel>
                <SelectIdentity
                  sx={{ width: '100%' }}
                  {...field}
                  value={value || null}
                  placeholder={<Trans id="Choose existing shareholder" />}
                  emptyState={<Trans id="No shareholders found" />}
                  paywall={
                    showUpgradeBanner ? (
                      <UpgradeBanner
                        feature="share_register"
                        customShareRegisterLimit={company.shareRegisterLimit}
                        navigateToBilling={() => navigate(`/settings/billing`)}
                      />
                    ) : undefined
                  }
                  handleCreate={createShareholder('buyerId')}
                  onChange={(_e, value) => {
                    if (value) {
                      field.onChange(value || '');
                    }
                  }}
                  identities={data.shareholders
                    .filter(sh => sh.id !== sellerId && Boolean(sh.identity))
                    .map(sh => ({ ...sh.identity!, value: sh.id }))}
                />
              </FormControl>
            )}
          />
        )}
        <Controller
          name="shareClassFrom"
          control={control}
          rules={{ required: true }}
          render={({ field, formState }) => {
            return (
              <FormControl error={Boolean(formState.errors?.shareClassFrom)}>
                <FormLabel>
                  {stg.kind === GqlTransactionKind.ShareClassConvert ? (
                    <Trans id="Share class (from)" />
                  ) : (
                    <Trans id="Share class" />
                  )}
                </FormLabel>
                <Select
                  {...field}
                  onChange={(_e, value) => {
                    if (value?.length) {
                      field.onChange(value);
                    }
                  }}
                  disabled={
                    stg.kind === GqlTransactionKind.Transfer || stg.kind === GqlTransactionKind.ShareClassConvert
                      ? !sellerId
                      : undefined
                  }
                >
                  {shareClassesFrom?.map(shareClass => (
                    <Option value={shareClass.name} key={shareClass.name}>
                      {shareClass.name}
                    </Option>
                  ))}
                  {stg.kind === GqlTransactionKind.Issue && !stg.snrShareCapitalChange && (
                    <Option value="create-new">
                      <Trans id="Create new" />
                    </Option>
                  )}
                </Select>
              </FormControl>
            );
          }}
        />
        {stg.kind === GqlTransactionKind.ShareClassConvert && (
          <Controller
            name="shareClassTo"
            control={control}
            rules={{ required: true }}
            render={({ field, formState }) => (
              <FormControl error={Boolean(formState.errors?.shareClassTo)}>
                <FormLabel>
                  <Trans id="Share class (to)" />
                </FormLabel>
                <Select
                  {...field}
                  onChange={(_e, value) => {
                    field.onChange(value);
                  }}
                  disabled={!shareClassFrom}
                >
                  {data.shareClasses
                    .filter(sc => sc.name !== shareClassFrom)
                    .map(shareClass => (
                      <Option value={shareClass.name} key={shareClass.name}>
                        {shareClass.name}
                      </Option>
                    ))}
                  <Option value="create-new">
                    <Trans id="Create new" />
                  </Option>
                </Select>
              </FormControl>
            )}
          />
        )}
        {(shareClassTo === 'create-new' || shareClassFrom === 'create-new') && (
          <>
            <Controller
              control={control}
              name="shareClassName"
              rules={{ required: true }}
              render={({ field, formState }) => (
                <FormControl error={Boolean(formState.errors?.shareClassName)}>
                  <FormLabel>
                    <Trans id="Share class name" />
                  </FormLabel>
                  <Input {...field} />
                </FormControl>
              )}
            />
            <Controller
              control={control}
              name="shareClassVoteWeight"
              rules={{ required: true }}
              render={({ field, formState }) => (
                <FormControl error={Boolean(formState.errors?.shareClassVoteWeight)}>
                  <FormLabel>
                    <Trans id="Share class vote weight" />
                  </FormLabel>
                  <NumericFormatInput {...field} lang={i18n.locale} />
                </FormControl>
              )}
            />
          </>
        )}
        <Controller
          control={control}
          name="numberOfShares"
          rules={{ required: true, min: 1, max: availableToAllocate }}
          render={({ field: { onChange, ...field }, formState }) => (
            <FormControl error={Boolean(formState.errors.numberOfShares)}>
              <FormLabel>
                <Trans id="Quantity" />
              </FormLabel>
              <NumericFormatInput
                {...field}
                onChange={event => {
                  if (event.target.value?.length) {
                    onChange(event.target.value);
                  }
                }}
                lang={i18n.locale}
                disabled={numberOfSharesDisabled}
                endDecorator={
                  !numberOfSharesDisabled &&
                  availableToAllocate && (
                    <Button
                      variant="soft"
                      color="neutral"
                      onClick={() => {
                        onChange(availableToAllocate);
                      }}
                    >
                      <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                        {availableToAllocate}
                      </NumberFormat>
                    </Button>
                  )
                }
              />
            </FormControl>
          )}
        />
        {stg.kind !== GqlTransactionKind.ShareClassConvert && stg.kind !== GqlTransactionKind.BlockPartition && (
          <Controller
            control={control}
            name="pricePerShare"
            render={({ field }) => (
              <FormControl>
                <FormLabel>
                  <Trans id="Price per share" />
                </FormLabel>
                <NumericFormatInput
                  {...field}
                  lang={i18n.locale}
                  endDecorator={
                    <Stack direction="row" gap={1} alignItems="center">
                      {stg.snrShareCapitalChange?.shareValue &&
                        field.value !== stg.snrShareCapitalChange.shareValue && (
                          <IconButton onClick={() => resetField('pricePerShare')} size="sm" sx={{ color: 'inherit' }}>
                            <RestartAltIcon />
                          </IconButton>
                        )}
                      {stg.snrShareCapitalChange ? (
                        stg.snrShareCapitalChange.currency
                      ) : (
                        <Controller
                          control={control}
                          name="currency"
                          render={({ field: { onChange, ...field } }) => (
                            <Select {...field} onChange={(_, v) => onChange(v)} variant="plain" color="neutral">
                              {currenciesShort.map(c => (
                                <Option key={c.code} value={c.code}>
                                  {c.code}
                                </Option>
                              ))}
                            </Select>
                          )}
                        />
                      )}
                    </Stack>
                  }
                />
              </FormControl>
            )}
          />
        )}
        <Controller
          control={control}
          name="comment"
          render={({ field: { onChange, ...field } }) => (
            <FormControl>
              <FormLabel>
                <Trans id="Comment" />
              </FormLabel>
              <RichTextInput value={field.value} onChange={onChange} />
            </FormControl>
          )}
        />
        <Box>
          <Button type="submit" loading={loading} color={success ? 'success' : undefined}>
            <Trans id="Create" />
          </Button>
        </Box>
      </Stack>
    </form>
  );
};
